import { useEffect, useState } from 'react';

export interface Remaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  done: boolean;
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Splits the time left into whole units (UTC maths, so the viewer's timezone doesn't matter). */
export function splitRemaining(ms: number): Remaining {
  const left = Math.max(0, Math.ceil(ms / SECOND) * SECOND);
  return {
    days: Math.floor(left / DAY),
    hours: Math.floor((left % DAY) / HOUR),
    minutes: Math.floor((left % HOUR) / MINUTE),
    seconds: Math.floor((left % MINUTE) / SECOND),
    done: left === 0,
  };
}

/** Share of the countdown already elapsed, 0–1, for a span that ends at `target`. */
export function countdownProgress(target: Date, now: number, spanDays: number): number {
  const span = spanDays * DAY;
  const elapsed = now - (target.getTime() - span);
  return Math.min(1, Math.max(0, elapsed / span));
}

/** Current time, re-rendering on each whole second. */
export function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let id: ReturnType<typeof setTimeout>;
    const schedule = () => {
      id = setTimeout(
        () => {
          setNow(Date.now());
          schedule();
        },
        SECOND - (Date.now() % SECOND),
      );
    };
    schedule();
    return () => clearTimeout(id);
  }, []);
  return now;
}

export function useCountdown(target: Date): Remaining & { now: number } {
  const now = useNow();
  return { ...splitRemaining(target.getTime() - now), now };
}
